// import OneSignal from "onesignal-cordova-plugin";
import { AppHeader } from "../../components/layout/AppHeader";
import {
	createFileRoute,
	Outlet,
	ScrollRestoration,
} from "@tanstack/react-router";
import { AppFooter } from "../../components/layout/AppFooter";
import { OnlineStatusProvider } from "../../context/OnlineStatusProvider";
import NetworkStatus from "../../components/NetworkStatus";
// import { OnlineStatusDebug } from "../../components/OnlineStatusDebug";
import { useEffect } from "react";
import { openDB, type IDBPDatabase } from "idb";
import type { ArticleParameters } from "../../api/api";
// eslint-disable-next-line no-duplicate-imports
import { getArticleContent, getArticleList } from "../../api/api";
import AppNavigationListener from "@/components/native/AppNavigationListener";
// import { AppNavigationDebugWrapper } from "@/components/AppNavigationDebugger";
// import { Capacitor } from "@capacitor/core";

const createArticlesDB = async (): Promise<IDBPDatabase> => {
	return openDB("articles-db", 1, {
		upgrade(db) {
			if (!db.objectStoreNames.contains("articles")) {
				const store = db.createObjectStore("articles", { keyPath: "id" });
				store.createIndex("date", "fetchDate");
			}
		},
	});
};

const fetchAndStoreArticles = async (): Promise<void> => {
	try {
		// Check if we have articles in the database
		const dbRead = await createArticlesDB();
		const txRead = dbRead.transaction("articles", "readonly");
		const articles = await txRead.store.getAll();
		if (articles.length > 0) {
			const firstArticle = articles[articles.length - 1] as {
				fetchDate: string;
			};
			const fetchDate = new Date(firstArticle.fetchDate);
			const now = new Date();
			const hoursDiff =
				(now.getTime() - fetchDate.getTime()) / (1000 * 60 * 60);

			// If articles are less than 24 hours old, return
			if (hoursDiff < 24) {
				console.log("Articles are fresh, no need to fetch");
				return;
			}
		}

		// Fetch article list (10 articles from page 1)
		const articleList = await getArticleList({} as ArticleParameters, 1, 15);

		if (!articleList || !articleList.posts) {
			throw new Error("Failed to fetch article list");
		}

		// Fetch content for each article
		const articlesWithContent = await Promise.all(
			articleList.posts.map(async (article) => {
				const content = await getArticleContent(article.ID);

				return {
					...article,
					content,
					fetchDate: new Date().getTime(),
				};
			})
		);

		// Store articles in IndexedDB
		const db = await createArticlesDB();
		const tx = db.transaction("articles", "readwrite");

		// Store each article
		await Promise.all(
			articlesWithContent.map((article) =>
				tx.store.put({ id: article.ID, ...article })
			)
		);

		await tx.done;
		console.log(
			"Stored",
			articlesWithContent.length,
			"articles in local storage"
		);
	} catch (error) {
		console.error("Error fetching and storing articles:", error);
	}
};

const AppLayout = (): JSX.Element => {
	useEffect(() => {
		fetchAndStoreArticles().catch((error) => {
			console.error("Error fetching and storing articles:", error);
		});
	}, []);

	// if (Capacitor.isNativePlatform()) {
	// 	OneSignal.Debug.setLogLevel(6);
	// 	// Initialize with your OneSignal App ID
	// 	OneSignal.initialize(import.meta.env.VITE_ONESIGNAL_APP_ID);
	// 	console.log(
	// 		"OneSignal initialized with app id:",
	// 		import.meta.env.VITE_ONESIGNAL_APP_ID
	// 	);
	// 	// Use this method to prompt for push notifications.
	// 	// We recommend removing this method after testing and instead use In-App Messages to prompt for notification permission.
	// 	OneSignal.Notifications.requestPermission(false)
	// 		.then((accepted: boolean) => {
	// 			console.log("User accepted notifications: " + accepted);
	// 		})
	// 		.catch((error) => {
	// 			console.error("Error requesting notifications permission:", error);
	// 		});
	// }

	return (
		<>
			<ScrollRestoration />
			<OnlineStatusProvider>
				{/* <OnlineStatusDebug /> */}
				<NetworkStatus />
				{/* <AppNavigationDebugWrapper> */}
				<AppNavigationListener />
				{/* </AppNavigationDebugWrapper> */}
				<AppHeader />
				<Outlet />
				<AppFooter />
			</OnlineStatusProvider>
		</>
	);
};

export const Route = createFileRoute("/(app)/_app")({
	component: AppLayout,
});
